import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { BiometricDeviceUpsertFormFields } from "@/components/biometricDevices/BiometricDeviceUpsertFormFields";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { fetchBiometricDeviceById, getBiometricDeviceById, updateBiometricDevice } from "@/lib/admin/biometricDevicesStore";
import {
  biometricDeviceFormErrorMessage,
  biometricDeviceFormToPayload,
  biometricDeviceToForm,
  type BiometricDeviceFormState,
} from "@/lib/admin/biometricDeviceForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { ArrowLeft, Fingerprint, Loader2, Save } from "lucide-react";
import { motion } from "framer-motion";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const EditBiometricDevice = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canUpdate = can(user, "biometric_device_can_update");
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [form, setForm] = useState<BiometricDeviceFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const record = useMemo(() => (id ? getBiometricDeviceById(id) : undefined), [id, version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let mounted = true;
    void (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const row = await fetchBiometricDeviceById(id);
        if (mounted) {
          setForm(biometricDeviceToForm(row));
          refresh();
        }
      } catch (e) {
        console.error("[EditBiometricDevice] Load failed", e);
        if (mounted) {
          if (isAxiosError(e) && e.response?.status === 404) setNotFound(true);
          else toast.error(biometricDeviceFormErrorMessage(e));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, refresh]);

  useEffect(() => {
    if (record && !form) setForm(biometricDeviceToForm(record));
  }, [record, form]);

  const handleCancel = useCallback(() => navigate(id ? `/biometric-devices/${id}` : "/biometric-devices"), [navigate, id]);

  const save = async () => {
    if (!id || !form || isSaving || !canUpdate) return;
    if (!form.model.trim()) {
      toast.error("Model is required.");
      return;
    }

    setIsSaving(true);
    try {
      const r = await updateBiometricDevice(id, biometricDeviceFormToPayload(form));
      toast.success(r.message);
      navigate(`/biometric-devices/${id}`);
    } catch (e) {
      console.error("[EditBiometricDevice] Save failed", e);
      toast.error(biometricDeviceFormErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canUpdate) {
    return (
      <PermissionDenied
        title="Cannot edit biometric devices"
        message="You don’t have permission to update biometric devices. Ask an administrator to grant biometric_device_can_update."
      />
    );
  }

  if (!id) {
    return (
      <Layout title="Edit biometric device">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing device id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Edit biometric device">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading device…
        </div>
      </Layout>
    );
  }

  if (notFound || !form) {
    return (
      <Layout title="Edit biometric device">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Biometric device not found.</div>
          <Button variant="glass" className="gap-2" asChild>
            <Link to="/biometric-devices">
              <ArrowLeft className="w-4 h-4" />
              Back to devices
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Edit: ${form.model}`}>
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground truncate">Edit biometric device</h1>
              <p className="text-sm text-muted-foreground mt-1 font-mono truncate">{form.model}</p>
            </div>
          </div>
          <Button variant="premium" className="gap-2 shrink-0" disabled={isSaving} onClick={() => void save()}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </Button>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Fingerprint className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-foreground text-sm">Device catalog entry</h2>
              <p className="text-xs text-muted-foreground">Update model, driver, wrapper class, and protocol.</p>
            </div>
          </div>
          <BiometricDeviceUpsertFormFields form={form} onChange={(patch) => setForm((f) => (f ? { ...f, ...patch } : f))} />
        </motion.div>
      </div>
    </Layout>
  );
};

export default EditBiometricDevice;
